import type { User } from './authentication/authUtils';
import { PENDO_API_KEY } from './config';
import type { AuthorizationStrategy } from './connectors/connectors';

declare global {
    interface Window {
        pendo: {
            initialize: (options: unknown) => void;
            updateOptions: (options: unknown) => void;
            track: (tagId: string, metadata: unknown) => void;
        };
    }
}

export const setupPendo = (user: User) => {
    bootstrap(user.systemSettings['com.boomi.platform.pendo.api.key'] || PENDO_API_KEY);
    initialize(user.user.account.email, user.user.account.accountId, user.user.account.status);
};

const bootstrap = (apiKey: string) => {
    /**
     * Bootstrap the `pendo` namespace for deferred execution of common methods until the Agent is fully loaded
     * from the CDN. The deferred methods are: a) initialize b) identify c) updateOptions d) pageLoad.
     */
    // biome-ignore-start lint: pendo
    (function (p, e, n, d, initialO) {
        let v, w, x, y, z;
        // @ts-ignore
        const o = initialO || (p[d] = p[d] || {});
        o._q = [];
        v = ['initialize', 'identify', 'updateOptions', 'pageLoad'];
        for (w = 0, x = v.length; w < x; ++w) {
            (function (m) {
                o[m] =
                    o[m] ||
                    function () {
                        o._q[m === v[0] ? 'unshift' : 'push'](
                            [m].concat([].slice.call(arguments, 0)),
                        );
                    };
            })(v[w]);
        }
        y = e.createElement(n);
        // @ts-ignore
        y.async = true;
        // @ts-ignore
        y.src = 'https://content.engagement.boomi.com/agent/static/' + apiKey + '/pendo.js';
        z = e.getElementsByTagName(n)[0];
        // @ts-ignore
        z.parentNode.insertBefore(y, z);
    })(window, document, 'script', 'pendo');
    // biome-ignore-end lint: pendo
};

const initialize = (visitor: string, account: string, accountStatus: string) => {
    if (!window.pendo) {
        console.log('Pendo instance not found!');
        return;
    }

    const sanitizeTransform = {
        attr: 'href',
        action: 'Replace',
        data() {
            const url = window.location.href;
            return url.replace('#', '?').replaceAll(';', '&');
        },
    };

    window.pendo.initialize({
        visitor: {
            id: visitor,
        },
        account: {
            id: account,
            status: accountStatus,
        },
        location: {
            transforms: [sanitizeTransform],
        },
    });
};

const track = (tagId: string, metadata: unknown = {}) => {
    if (!window.pendo) {
        console.log('Pendo instance not found!');
        return;
    }

    window.pendo?.track(tagId, metadata);
};

export const trackSetIDP = (props: { IDP_name: string; status: 'Success' | 'Failed' }) =>
    track('connect_set_IDP', { provider_type: 'OAUTH', ...props });

export const trackCreateConfig = (props: {
    connector_type: 'Pre-configured' | 'Manual-configured';
    config_name: string;
    auth_method: AuthorizationStrategy;
    total_tools_selected: number;
    status: 'Success' | 'Failed';
}) => track('connect_create_config', props);

export const trackConnectorEnableDisable = (props: {
    connector_name: string;
    connector_current_status: 'enabled' | 'disabled';
}) => track('connect_connector_enable_disable', props);
